import { z } from 'zod';
import { isValidPhoneAU } from '@workspace/lib/helper';
import { FORM_FIELD_KINDS } from '../constants';

const fieldValidations = {
  firstname: (r: boolean) => {
    let s = z.string().max(50, 'First name is limited to 50 characters.');
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  lastname: (r: boolean) => {
    let s = z.string().max(50, 'Last name is limited to 50 characters.');
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  email: (r: boolean) => {
    let s = z
      .string()
      .email('Invalid email format. Example: name@example.com.')
      .max(255, 'Email is limited to 255 characters.');
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  phone: (r: boolean) => {
    let s = z.string();
    let schema = r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
    return schema.refine((v) => !v || isValidPhoneAU(String(v || '')), {
      message: 'Invalid phone number.',
    });
  },
  company: (r: boolean) => {
    let s = z.string().max(255, 'Company Name is limited to 255 characters.');
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  message: (r: boolean) => {
    let s = z.string().max(1000, 'Message is limited to 1000 characters.');
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  state: (r: boolean) => {
    let s = z.string();
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  area_of_work: (r: boolean) => {
    let s = z.string();
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  where_did_you_hear_about_porta_: (r: boolean) => {
    let s = z.string();
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  enquiry_type___dropdown: (r: boolean) => {
    let s = z.string();
    return r ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
  },
  newsletter: (r: boolean) => z.boolean().default(false),
  honeypot: (r: boolean) => z.string().optional(),
} as const;

export const contactFormSchema = z.object({
  firstname: fieldValidations.firstname(true),
  lastname: fieldValidations.lastname(true),
  email: fieldValidations.email(true),
  phone: fieldValidations.phone(true),
  company: fieldValidations.company(false),
  state: fieldValidations.state(true),
  area_of_work: fieldValidations.area_of_work(true),
  where_did_you_hear_about_porta_: fieldValidations.where_did_you_hear_about_porta_(false),
  enquiry_type___dropdown: fieldValidations.enquiry_type___dropdown(true),
  message: fieldValidations.message(true),
  newsletter: fieldValidations.newsletter(false),
  honeypot: fieldValidations.honeypot(false),
});

export const generateContactFormSchema = (
  fields?: ReadonlyArray<{ name: string; required?: boolean; kind?: string }>,
): z.ZodObject<any> => {
  if (!fields || fields.length === 0) {
    return contactFormSchema;
  }

  const shape: any = {
    newsletter: fieldValidations.newsletter(false),
    honeypot: fieldValidations.honeypot(false),
  };

  fields.forEach((f) => {
    const name = f.name as keyof typeof fieldValidations;
    if (fieldValidations[name]) {
      shape[f.name] = fieldValidations[name](!!f.required);
    } else if (f.kind === FORM_FIELD_KINDS.CHECKBOX) {
      // Checkbox fields hold boolean values. When required, the box must be checked (true).
      const boolSchema = z.boolean().default(false);
      shape[f.name] = f.required
        ? boolSchema.refine((v) => v === true, { message: 'This field is required.' })
        : boolSchema;
    } else if (f.kind === FORM_FIELD_KINDS.FILE) {
      // File fields hold a File object or null (browser environment).
      // z.instanceof(File) is only available client-side; use z.custom for SSR safety.
      const fileSchema = z.custom<File | null>(
        (v) => v == null || (typeof File !== 'undefined' && v instanceof File),
        { message: 'Please upload a valid file.' },
      );
      shape[f.name] = f.required
        ? fileSchema.refine((v) => v != null, { message: 'This field is required.' })
        : fileSchema.optional().nullable();
    } else {
      // Fallback for unknown string-type fields
      const s = z.string();
      shape[f.name] = f.required ? s.min(1, 'This field is required.') : s.optional().or(z.literal(''));
    }
  });

  return z.object(shape);
};

export type ContactFormData = z.infer<typeof contactFormSchema>;
